/**
 * User API Client
 *
 * API client for user-related backend endpoints.
 * Communicates with /api/v1/auth endpoints.
 */

import { api } from '@/lib/axios'
import type { DigestSettingsResponse, UserPreferences } from '../types/auth.types'

/**
 * User API Client
 */
export const userApi = {
  /**
   * Get current user preferences from backend
   * Requires Authorization header with Firebase ID token
   */
  async getCurrentUser(): Promise<UserPreferences> {
    const response = await api.get<UserPreferences>('/api/v1/auth/me')
    return response.data
  },

  /**
   * Delete current user account
   * Deletes both Firebase user and backend user preferences
   */
  async deleteAccount(): Promise<void> {
    await api.delete('/api/v1/auth/me')
  },

  // `getCreditStatus` (GET /preferences/{id}/credits) lived here with zero callers.
  // Deleted, not wired up: `getCurrentUser` already returns the same three numbers
  // (`remaining_credits`, `daily_credit_limit`, `credits_used_today`) and runs on
  // every sign-in, so keeping it was an invitation to spend a round trip learning
  // what is already on hand. The route itself still exists.

  /**
   * Read the morning digest opt-in state.
   */
  async getDigestSettings(userId: string): Promise<DigestSettingsResponse> {
    const response = await api.get<DigestSettingsResponse>(`/api/v1/preferences/${userId}/digest`)
    return response.data
  },

  /**
   * Turn the morning digest on or off. Enabling requires an email address.
   */
  async updateDigestSettings(
    userId: string,
    input: { enabled: boolean; email: string | null }
  ): Promise<DigestSettingsResponse> {
    const response = await api.put<DigestSettingsResponse>(
      `/api/v1/preferences/${userId}/digest`,
      input
    )
    return response.data
  },

  /**
   * Persist the keywords the digest job reads.
   *
   * The app keeps keywords in localStorage (P1-2), but `digest_service` iterates
   * `user.registered_keywords` on the server — without this write an opted-in user
   * gets an empty digest forever.
   *
   * Called by `useKeywordServerSync` on every keyword edit (P3-20), and by the digest
   * screen's 「이 기기 키워드로 맞추기」 when another device wrote last. A 403
   * `KEYWORD_LIMIT_EXCEEDED` is possible and has to reach the user: the server refuses
   * to grow a list past the tier limit, and a background write that fails unseen is
   * how the digest ends up on an old list with nothing on screen saying so.
   */
  async saveRegisteredKeywords(userId: string, keywords: string[]): Promise<void> {
    await api.put(`/api/v1/preferences/${userId}`, { registered_keywords: keywords })
  },
}
